import type { MarkdownDocument } from '@markbird/shared';
import type { RemoteDoc } from '../syncDecide';
import { decryptText, encryptText, type Encrypted } from './vaultKey';

/**
 * What the server may see, and what it may not.
 *
 * Sync needs a few fields in the clear to work at all: which account a record
 * belongs to, when it changed, and whether it is a tombstone — the rules in
 * syncDecide.ts are about clocks, and a clock nobody can read decides nothing.
 * Everything a person wrote — the text, the title, the folder names, the tags —
 * goes into one encrypted blob.
 *
 * That leaves an honest residue: the server can see *that* an account has N
 * documents and when each changed. Hiding that needs a different protocol, not
 * a different cipher, and pretending otherwise would be worse than saying it.
 */

/** The fields that carry what someone wrote. */
const SECRET_FIELDS = ['title', 'content', 'folder', 'tags', 'aliases', 'rank'] as const;

export interface EncryptedDoc extends RemoteDoc {
  /** Everything in `SECRET_FIELDS`, as one AES-GCM record. */
  secret: Encrypted;
}

export function isEncrypted(doc: RemoteDoc | EncryptedDoc): doc is EncryptedDoc {
  return typeof (doc as EncryptedDoc).secret?.data === 'string';
}

/** A document as it should be stored: clocks in the clear, the writing sealed. */
export async function encryptDoc(doc: RemoteDoc, key: CryptoKey): Promise<EncryptedDoc> {
  const secretPart: Record<string, unknown> = {};
  for (const field of SECRET_FIELDS) {
    if (doc[field] !== undefined) secretPart[field] = doc[field];
  }
  const sealed = await encryptText(key, JSON.stringify(secretPart));

  const shell: EncryptedDoc = { ...doc, secret: sealed } as EncryptedDoc;
  for (const field of SECRET_FIELDS) delete (shell as unknown as Record<string, unknown>)[field];
  // A document still needs the two fields the type insists on, and a reader
  // without the key should see nothing in them.
  shell.title = '';
  shell.content = '';
  return shell;
}

/**
 * Open a stored document, or null when this device cannot.
 *
 * Null is not an error to swallow: the caller keeps its local copy untouched
 * and says the vault is locked. Overwriting a readable note with an unreadable
 * one is the one outcome encryption must never produce.
 */
export async function decryptDoc(
  doc: RemoteDoc | EncryptedDoc,
  key: CryptoKey | null,
): Promise<MarkdownDocument | null> {
  if (!isEncrypted(doc)) return doc;
  if (!key) return null;
  const plain = await decryptText(key, doc.secret);
  if (plain === null) return null;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(plain) as Record<string, unknown>;
  } catch {
    return null;
  }
  const opened = { ...doc } as Record<string, unknown>;
  delete opened.secret;
  for (const field of SECRET_FIELDS) delete opened[field];
  return { ...opened, ...parsed } as unknown as MarkdownDocument;
}
